import React from 'react';
import { PortvilleRoomWithResidency, ROOM_STATUS_LABELS, ROOM_STATUS_COLORS } from '../../../types/portvilleRoom';

interface RoomTableProps {
  rooms: PortvilleRoomWithResidency[];
  onView?: (room: PortvilleRoomWithResidency) => void;
  onEdit: (room: PortvilleRoomWithResidency) => void;
  onCreateContract: (room: PortvilleRoomWithResidency) => void;
  onContract: (room: PortvilleRoomWithResidency) => void;
  onViewContract: (room: PortvilleRoomWithResidency, contractType?: 'original' | 'extension1' | 'extension2') => void;
  onMoveInInspection: (room: PortvilleRoomWithResidency) => void;
  onMoveOutInspection: (room: PortvilleRoomWithResidency) => void;
  onReviewContractCancellation: (room: PortvilleRoomWithResidency, action: 'approve' | 'reject') => void;
  onTerminateContract: (room: PortvilleRoomWithResidency) => void;
  onViewHistory: (room: PortvilleRoomWithResidency) => void;
  onExtensionRequest: (room: PortvilleRoomWithResidency, round: 1 | 2) => void;
  onExtensionContract: (room: PortvilleRoomWithResidency, round: 1 | 2) => void;
  loading: boolean;
}

const RoomTable: React.FC<RoomTableProps> = ({ rooms, onView, onEdit, onCreateContract, onContract, onViewContract, onMoveInInspection, onMoveOutInspection, onReviewContractCancellation, onTerminateContract, onViewHistory, onExtensionRequest, onExtensionContract, loading }) => {
  const formatDate = (date: Date) => {
    return date.toLocaleDateString('ko-KR');
  };

  const getDaysUntilExpiry = (endDate: Date) => {
    const today = new Date();
    const diffTime = endDate.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow">
        <div className="p-6 text-center">
          <div className="text-lg">로딩 중...</div>
        </div>
      </div>
    );
  }

  if (rooms.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow">
        <div className="p-6 text-center">
          <div className="text-gray-500">등록된 호실이 없습니다.</div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow overflow-hidden">
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-2 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              호실번호
            </th>
            <th className="px-2 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              상태
            </th>
            <th className="px-2 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              입주자 정보
            </th>
            <th className="px-2 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              입주기간
            </th>
            <th className="hidden lg:table-cell px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              계약 만료까지
            </th>
            <th className="px-2 lg:px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
              작업
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {rooms.map((room) => {
            const daysUntilExpiry = room.occupancyPeriod?.endDate 
              ? getDaysUntilExpiry(room.occupancyPeriod.endDate) 
              : null;
            const canCreateContract = (room.status === 'vacant' || room.status === 'preparing') && !room.currentResidencyId;
	            const canViewContract = !!room.currentResidencyId && (room.status === 'occupied' || !!room.contractSignedAt);
	            const hasMoveOutInspection = !!room.moveOutInspectionSignedAt;
	            const pendingContractCancellation = room.pendingContractCancellation;
	            const canMoveOutInspect = room.status === 'occupied' && !!room.currentResidencyId && !hasMoveOutInspection;
	            const canTerminateContract = room.status === 'occupied' && !!room.currentResidencyId && hasMoveOutInspection;
            const extension1Status = room.extensionStatus;
            const extension2Status = room.secondExtensionStatus;
            
            return (
              <tr key={room.id} className="hover:bg-gray-50">
                <td className="px-2 lg:px-6 py-4 whitespace-nowrap">
                  <div className="text-sm font-medium text-gray-900">
                    {room.roomNumber}호
                  </div>
                </td>
                <td className="px-2 lg:px-6 py-4 whitespace-nowrap">
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                    ROOM_STATUS_COLORS[room.status]
                  }`}>
                    {ROOM_STATUS_LABELS[room.status]}
                  </span>
                </td>
                <td className="px-2 lg:px-6 py-4">
                  {room.status !== 'vacant' && room.currentResident ? (
                    <div className="text-sm">
                      <div className="font-medium text-gray-900">
                        {room.currentResident.name}
                      </div>
                      <div className="text-gray-500">
                        {room.currentResident.phoneNumber}
                      </div>
                      <div className="text-gray-500">
                        {room.currentResident.organization}
                      </div>
                      {/* 퇴거신청 상태 표시 */}
                      {(room as any).hasPendingMoveOut && (
                        <div className="mt-1">
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                            <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                            </svg>
                            퇴거신청
                          </span>
                        </div>
                      )}
                      {/* 계약 연장 신청 상태 표시 */}
	                      {extension1Status === 'applied' && (
                        <div className="mt-1">
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                            <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            1차연장신청
                          </span>
                        </div>
	                      )}
	                      {pendingContractCancellation && (
	                        <div className="mt-1">
	                          <span
	                            className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800"
	                            title={pendingContractCancellation.reason || '계약 포기신청'}
	                          >
	                            포기신청
	                          </span>
	                          {pendingContractCancellation.reason && (
	                            <div className="mt-1 w-full whitespace-normal break-words text-xs text-red-700" title={pendingContractCancellation.reason}>
	                              사유: {pendingContractCancellation.reason}
	                            </div>
	                          )}
	                        </div>
	                      )}
                      {extension1Status === 'approved' && (
                        <div className="mt-1">
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                            1차연장승인
                          </span>
                        </div>
                      )}
                      {extension1Status === 'rejected' && (
                        <div className="mt-1">
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                            <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                            1차연장거부
                          </span>
                        </div>
                      )}
                      {extension1Status === 'contracted' && (
                        <div className="mt-1">
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            1차연장완료
                          </span>
                        </div>
                      )}

                      {/* 2차 계약 연장 신청 상태 표시 */}
                      {extension2Status === 'applied' && (
                        <div className="mt-1">
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-orange-50 text-orange-800 border border-orange-200">
                            <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            2차연장신청
                          </span>
                        </div>
                      )}
                      {extension2Status === 'approved' && (
                        <div className="mt-1">
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-50 text-green-800 border border-green-200">
                            <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                            2차연장승인
                          </span>
                        </div>
                      )}
                      {extension2Status === 'rejected' && (
                        <div className="mt-1">
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-50 text-red-800 border border-red-200">
                            <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                            2차연장거부
                          </span>
                        </div>
                      )}
                      {extension2Status === 'contracted' && (
                        <div className="mt-1">
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-50 text-blue-800 border border-blue-200">
                            <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            2차연장완료
                          </span>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="text-sm text-gray-400">-</div>
                  )}
                </td>
                <td className="px-2 lg:px-6 py-4 whitespace-nowrap">
                  {room.occupancyPeriod ? (
                    <div className="text-sm text-gray-900">
                      <div>시작: {formatDate(room.occupancyPeriod.startDate)}</div>
                      <div className="flex items-center">
                        <span>종료: {formatDate(room.occupancyPeriod.endDate)}</span>
                        {room.moveOutInfo && (
                          <span className="ml-2 inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800" title="퇴거 승인으로 계약 종료일이 변경됨">
                            <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.464 0L4.35 16.5c-.77.833.192 2.5 1.732 2.5z" />
                            </svg>
                            변경됨
                          </span>
                        )}
                      </div>
                      {/* 모바일/태블릿용 계약 만료까지 정보 */}
                      <div className="lg:hidden mt-2">
                        {daysUntilExpiry !== null ? (
                          <div className={`text-sm font-medium ${
                            daysUntilExpiry <= 7
                              ? 'text-red-600'
                              : daysUntilExpiry <= 30
                              ? 'text-yellow-600'
                              : 'text-green-600'
                          }`}>
                            만료까지: {daysUntilExpiry > 0 ? `${daysUntilExpiry}일 남음` :
                             daysUntilExpiry === 0 ? '오늘 만료' :
                             `${Math.abs(daysUntilExpiry)}일 경과`}
                          </div>
                        ) : null}
                      </div>
                    </div>
                  ) : (
                    <div className="text-sm text-gray-400">-</div>
                  )}
                </td>
                <td className="hidden lg:table-cell px-6 py-4 whitespace-nowrap">
                  {daysUntilExpiry !== null ? (
                    <div className={`text-sm font-medium ${
                      daysUntilExpiry <= 7
                        ? 'text-red-600'
                        : daysUntilExpiry <= 30
                        ? 'text-yellow-600'
                        : 'text-green-600'
                    }`}>
                      {daysUntilExpiry > 0 ? `${daysUntilExpiry}일 남음` :
                       daysUntilExpiry === 0 ? '오늘 만료' :
                       `${Math.abs(daysUntilExpiry)}일 경과`}
                    </div>
                  ) : (
                    <div className="text-sm text-gray-400">-</div>
                  )}
                </td>
                <td className="px-2 lg:px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                  <div className="flex flex-col space-y-1">
                    {/* 첫 번째 줄: 계약진행 상태일 때의 순서: 입주점검 → 계약서 날인 */}
                    <div className="flex justify-end space-x-2">
                      {canCreateContract && (
                        <button
                          onClick={() => onCreateContract(room)}
                          className="text-indigo-600 hover:text-indigo-900 font-medium"
                          title="관리자 수기 계약 등록"
                        >
                          계약등록
                        </button>
                      )}
                      {canViewContract && (
                        <button
                          onClick={() => onViewContract(room)}
                          className="text-green-600 hover:text-green-900"
                        >
                          계약서
                        </button>
                      )}
                      {canMoveOutInspect && (
                        <button
                          onClick={() => onMoveOutInspection(room)}
                          className="text-red-600 hover:text-red-900"
                          title="퇴거점검"
                        >
                          퇴거점검
                        </button>
                      )}
                      {hasMoveOutInspection && (
                        <span
                          className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800"
                          title="퇴거점검 완료"
                        >
                          퇴거점검완료
                        </span>
                      )}
                      {canTerminateContract && (
                        <button
                          onClick={() => onTerminateContract(room)}
                          className="text-red-700 hover:text-red-900 font-medium"
                          title="퇴거점검 완료 후 계약종료"
                        >
                          계약종료
                        </button>
                      )}
		                      {room.status === 'contracting' && pendingContractCancellation && !room.contractSignedAt && (
	                        <>
	                          <button
	                            onClick={() => onReviewContractCancellation(room, 'approve')}
	                            className="text-red-700 hover:text-red-900 font-medium"
	                            title="계약 포기신청 승인"
	                          >
	                            포기승인
	                          </button>
	                          <button
	                            onClick={() => onReviewContractCancellation(room, 'reject')}
	                            className="text-gray-700 hover:text-gray-900 font-medium"
	                            title="계약 포기신청 반려"
	                          >
	                            포기반려
	                          </button>
	                        </>
	                      )}
	                      {room.status === 'contracting' && !pendingContractCancellation && !room.moveInInspectionSignedAt && (
	                        <button
	                          onClick={() => onMoveInInspection(room)}
                          className="text-orange-600 hover:text-orange-900"
                        >
                          입주점검
                        </button>
                      )}
	                      {room.status === 'contracting' && room.moveInInspectionSignedAt && !room.contractSignedAt && !pendingContractCancellation && (
                        <button
                          onClick={() => onContract(room)}
                          className="text-purple-600 hover:text-purple-900"
                        >
                          계약서 날인
                        </button>
                      )}
                      {room.status === 'contracting' && room.moveInInspectionSignedAt && room.contractSignedAt && (
                        <button
                          onClick={() => onViewContract(room)}
                          className="text-green-600 hover:text-green-900"
                        >
                          계약서 확인
                        </button>
                      )}
                      {/* 계약 연장 신청 확인 버튼 */}
                      {extension1Status === 'applied' && (
                        <button
                          onClick={() => onExtensionRequest(room, 1)}
                          className="text-orange-600 hover:text-orange-900 font-medium"
                          title="1차 계약 연장 신청 확인"
                        >
                          연장요청확인
                        </button>
                      )}
                      {/* 계약 연장 날인 버튼 */}
                      {extension1Status === 'approved' && (
                        <button
                          onClick={() => onExtensionContract(room, 1)}
                          className="text-green-600 hover:text-green-900 font-medium"
                          title="1차 계약 연장 날인"
                        >
                          연장계약날인
                        </button>
                      )}
                      {/* 2차 계약 연장 신청 확인 버튼 */}
                      {extension2Status === 'applied' && (
                        <button
                          onClick={() => onExtensionRequest(room, 2)}
                          className="text-orange-700 hover:text-orange-900 font-medium"
                          title="2차 계약 연장 신청 확인"
                        >
                          2차연장확인
                        </button>
                      )}
                      {/* 2차 계약 연장 날인 버튼 */}
                      {extension2Status === 'approved' && (
                        <button
                          onClick={() => onExtensionContract(room, 2)}
                          className="text-green-700 hover:text-green-900 font-medium"
                          title="2차 계약 연장 날인"
                        >
                          2차연장날인
                        </button>
                      )}
                    </div>
                    
                    {/* 두 번째 줄: 공통 버튼들 */}
                    <div className="flex justify-end space-x-2">
                      <button
                        onClick={() => onViewHistory(room)}
                        className="text-gray-600 hover:text-gray-900"
                        title="입주 히스토리 보기"
                      >
                        히스토리
                      </button>
                      {onView && (
                        <button
                          onClick={() => onView(room)}
                          className="text-blue-600 hover:text-blue-900"
                          title="상세보기"
                        >
                          상세
                        </button>
                      )}
                      <button
                        onClick={() => onEdit(room)}
                        className="text-green-600 hover:text-green-900"
                        title="수정"
                      >
                        수정
                      </button>
                    </div>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      </div>
    </div>
  );
};

export default RoomTable; 
